import { useEffect, useState, type FormEvent } from "react";

import {
  createPlaylist,
  patchPlaylist,
  type PlaylistCreateInput,
} from "../../api/programming.js";
import { Button } from "../../design-system/button.js";
import {
  Field,
  NumberInput,
  Select,
  Switch,
  TextInput,
} from "../../design-system/form.js";
import { Modal } from "../../design-system/modal.js";
import { Notice } from "../../design-system/surfaces.js";
import type { Playlist, PlaylistMode } from "../../api/types.js";

/**
 * What a playlist is, in the four things that decide what it plays: a name, how it
 * draws from its items, and how long it must wait before repeating a track or an
 * artist. The separation rules are the playlist's, not a feed's — two feeds drawing on
 * the same playlist are held to the same rule, which is the point of the playlist
 * being the object rather than the feed.
 */
const MODE_HINTS: Record<PlaylistMode, string> = {
  ordered: "Plays its items in the order they are in.",
  shuffle: "Draws at random, without weighting.",
  weighted: "Draws at random, in proportion to each item's weight.",
};

export function PlaylistDialog({
  onClose,
  onSaved,
  open,
  playlist,
}: {
  onClose: () => void;
  onSaved: (playlist: Playlist) => void;
  open: boolean;
  /** Absent when a playlist is being made; present when one is being changed. */
  playlist?: Playlist | null;
}) {
  const [name, setName] = useState("");
  const [mode, setMode] = useState<PlaylistMode>("shuffle");
  const [trackSeparation, setTrackSeparation] = useState("0");
  const [artistSeparation, setArtistSeparation] = useState("0");
  const [enabled, setEnabled] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(playlist?.name ?? "");
    setMode(playlist?.mode ?? "shuffle");
    setTrackSeparation(String(playlist?.minTrackSeparationMinutes ?? 0));
    setArtistSeparation(String(playlist?.minArtistSeparationMinutes ?? 0));
    setEnabled(playlist?.enabled ?? true);
    setError(null);
  }, [open, playlist]);

  const ready = name.trim() !== "" && !busy;

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!ready) return;
    setBusy(true);
    setError(null);
    const input: PlaylistCreateInput = {
      name: name.trim(),
      mode,
      min_track_separation_minutes: Math.max(0, Number(trackSeparation) || 0),
      min_artist_separation_minutes: Math.max(0, Number(artistSeparation) || 0),
      enabled,
    };
    const saving = playlist
      ? patchPlaylist(playlist.id, input)
      : createPlaylist(input);
    saving
      .then((saved) => {
        if (saved) onSaved(saved);
        onClose();
      })
      .catch((cause: unknown) => {
        setError(
          cause instanceof Error
            ? cause.message
            : "The playlist was not saved.",
        );
      })
      .finally(() => {
        setBusy(false);
      });
  }

  return (
    <Modal
      onClose={onClose}
      open={open}
      title={playlist ? "Playlist settings" : "New playlist"}
    >
      <form className="flex flex-col gap-5" noValidate onSubmit={submit}>
        <Field htmlFor="playlist-name" label="Name">
          <TextInput
            autoFocus
            id="playlist-name"
            onChange={(event) => {
              setName(event.target.value);
            }}
            placeholder="Late night"
            required
            value={name}
          />
        </Field>

        <Field hint={MODE_HINTS[mode]} htmlFor="playlist-mode" label="Mode">
          <Select
            id="playlist-mode"
            onChange={(event) => {
              setMode(event.target.value as PlaylistMode);
            }}
            value={mode}
          >
            <option value="ordered">Ordered</option>
            <option value="shuffle">Shuffle</option>
            <option value="weighted">Weighted</option>
          </Select>
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            hint="How long before the same track may play again."
            htmlFor="playlist-track-separation"
            label="Track separation"
          >
            <NumberInput
              id="playlist-track-separation"
              min={0}
              onChange={(event) => {
                setTrackSeparation(event.target.value);
              }}
              unit="min"
              value={trackSeparation}
            />
          </Field>
          <Field
            hint="How long before the same artist may play again."
            htmlFor="playlist-artist-separation"
            label="Artist separation"
          >
            <NumberInput
              id="playlist-artist-separation"
              min={0}
              onChange={(event) => {
                setArtistSeparation(event.target.value);
              }}
              unit="min"
              value={artistSeparation}
            />
          </Field>
        </div>

        <Switch
          checked={enabled}
          description="A feed can only be pointed at an enabled playlist."
          label="Enabled"
          onChange={setEnabled}
        />

        {error ? <Notice tone="danger">{error}</Notice> : null}

        <div className="flex items-center justify-end gap-2 border-t border-border pt-4">
          <Button onClick={onClose} variant="ghost">
            Cancel
          </Button>
          <Button disabled={!ready} type="submit">
            {busy ? "Saving…" : playlist ? "Save playlist" : "Create playlist"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
